import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useConfirmationModal } from "@/context/confirmationModalContext";
import { formatAppointmentRequestAvailableDaysText } from "@/helper/appointmentRequestAvailableDays";
import { AppointmentRequestNoAvailableDaysMessage } from "@/components/appointmentRequest/appointmentRequestNoAvailableDaysMessage";
import {
  AppointmentListRow,
  AppointmentLocationLine,
  CustomerInformationNote,
  ExaminationList,
  ExaminationVideoList,
  RowActions,
} from "@/components/appointmentRequest/appointmentListItemParts";
import styles from "@/components/appointmentRequest/appointmentListItem.module.css";

interface Props {
  item: any;
  wide: boolean;
  onDecline: (item: any) => void;
  onPlayVideo: (url: string) => void;
}

export default function OpenAppointmentRequestListItem(props: Props) {
  const { item } = props;
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { showConfirmationModalAsync } = useConfirmationModal();
  const hasAvailableDays = Boolean(item?.available_day_set?.length);
  const availableDaysText = formatAppointmentRequestAvailableDaysText(item);

  const decline = async () => {
    const confirmed = await showConfirmationModalAsync(
      t("appointment_request_decline_confirmation"),
    );
    if (confirmed) {
      props.onDecline(item);
    }
  };

  return (
    <AppointmentListRow dataTestId={`appointment-request-open-${item.id}`}>
      <h4 className={styles.title}>{item.title}</h4>
      <span
        className={
          hasAvailableDays
            ? styles.dateLine
            : `${styles.dateLine} ${styles.dateLineError}`
        }
      >
        {availableDaysText ?? <AppointmentRequestNoAvailableDaysMessage />}
      </span>
      <AppointmentLocationLine item={item} />
      <ExaminationList examinations={item.examination_set} />
      <CustomerInformationNote item={item} />
      <ExaminationVideoList
        examinations={item.examination_set}
        onPlay={props.onPlayVideo}
      />
      <RowActions
        wide={props.wide}
        actions={[
          {
            key: "decline",
            tooltip: t("decline"),
            icon: "ban",
            onClick: decline,
          },
          {
            key: "choose",
            dataTestId: `appointment-request-choose-${item.id}`,
            tooltip: hasAvailableDays
              ? t("choose")
              : t("error_all_slots_taken_please_contact"),
            disabled: !hasAvailableDays,
            onClick: () => navigate(`/appointmentrequestopen/${item.id}/pick`),
          },
        ]}
      />
    </AppointmentListRow>
  );
}
